import React, { useState } from "react";
import RailGroup from "./RailGroup";
import ScoreBreakdown from "./ScoreBreakdown";
import { fitView, parseMoney, parseYear } from "./railModel.mjs";

/*
 * FIT (frames 1b and 1c open; the line in 1a, 1d, 1g): the score where it applies,
 * the firmographics, the description.
 *
 * Industry is stored as an enum (COMPUTER_NETWORK_SECURITY) and shown as words;
 * the scoring row it matched ("Cybersecurity") belongs to the breakdown only, so
 * the rail never names a category nobody stored. Funding is read from both keys
 * the stores use (total_funding, total_money_raised), with the stage the scorer
 * derived from it ("Series B+ · $38M"). Each is worked out in record/railModel.mjs
 * fitView, which says why.
 *
 * AN UNKNOWN FACT IS AN "Add {thing}" that writes through properties_set (PR #154:
 * merged into the bag, never replacing it), and the score is live, so "Would
 * score" moves with it: the brief's reason for asking at all. Industry, headcount
 * and country are columns with their own editors on Profile, which is where the
 * card's one Edit goes.
 *
 * WHERE THE SCORE HAS LEFT THE HEADER (clients, churned, lost) it lives here as
 * "Would score 33 today", and its Breakdown opens inline; on an associated
 * colleague's record it says whose score it is. Where the header features the
 * score, this group leaves it out. A Breakdown asked for from anywhere (a lead
 * block's "Breakdown" link) opens this group, so the answer is never inside a
 * closed card.
 *
 * Props:
 *   record   the shell's bundle: company (lead_score), score (scoreView),
 *            view.prominence, ui.score (the breakdown is open)
 *   ops      ops.setProps("company", id, props) → promise, ops.toggleBreakdown,
 *            ops.setTab("profile") (the card's Edit)
 *   state    "open" | "summary"
 *   sheet    boolean, phone
 */

const PARSE = {
  money: { read: (v) => parseMoney(v), inputMode: "decimal", error: "A number, like $4M or 4000000." },
  year: { read: (v) => parseYear(v), inputMode: "numeric", error: "A year, like 2019." },
  text: { read: (v) => (v.trim() ? v.trim() : null), inputMode: undefined, error: "Type something first." },
};

function AddFact({ add, onSave, hint = null }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const how = PARSE[add.parse] || PARSE.text;
  if (!open) {
    return (
      <span>
        <button type="button" className="rec-link" onClick={() => setOpen(true)}>{add.label}</button>
      </span>
    );
  }
  const save = async () => {
    const parsed = how.read(value);
    if (parsed === null) {
      setError(how.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(parsed);
      setOpen(false);
      setValue("");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="rec-stack">
      <span className="rec-editrow">
        <input
          className="rec-input"
          autoFocus
          aria-label={add.label}
          placeholder={add.placeholder}
          inputMode={how.inputMode}
          value={value}
          disabled={busy}
          onChange={(e) => { setValue(e.target.value); setError(null); }}
          onKeyDown={(e) => e.key === "Enter" && save()}
        />
        <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={busy || !value.trim()} onClick={save}>Save</button>
        <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" disabled={busy} onClick={() => { setOpen(false); setError(null); }}>Cancel</button>
      </span>
      {hint && <span className="rec-field__hint">{hint}</span>}
      {error && <span className="rec-err">{error}</span>}
    </span>
  );
}

/** The Profile tab, scrolled to: where every column-backed fact has its editor. */
function toProfile(event, ops) {
  ops.setTab("profile");
  const root = event.currentTarget.closest(".rec");
  const tabs = root && root.querySelector(".rec-tabwrap");
  if (tabs && tabs.scrollIntoView) setTimeout(() => tabs.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
}

export default function Fit({ record, ops, state = "summary", sheet = false }) {
  const co = record.company || {};
  const s = record.score;
  const prominence = record.view.prominence;
  const view = fitView({ company: co, score: s, leadScore: co.lead_score, prominence });
  // The breakdown lives here only where the header has no badge to open it under.
  const scoreHere = prominence === "fit_only" || prominence === "scored_on";
  const breakdownOpen = scoreHere && !!(record.ui && record.ui.score) && !!(s && s.show);
  const [own, setOwn] = useState(state === "open");
  const save = (props) => ops.setProps("company", co.id, props);
  const fundingChip = ((co.lead_score && co.lead_score.chips) || []).find((c) => c.group === "funding");
  const ageChip = ((co.lead_score && co.lead_score.chips) || []).find((c) => c.group === "age");
  const hintFor = (key) => {
    const chip = key === "funding" ? fundingChip : key === "founded" ? ageChip : null;
    return chip && chip.ceiling ? `Fit counts this for up to ${chip.ceiling} points, so the score updates.` : null;
  };

  return (
    <RailGroup
      id="fit"
      title="Fit"
      summary={view.summary}
      open={own || breakdownOpen}
      onToggle={(next) => {
        setOwn(next);
        // Closing the card closes the breakdown inside it, or it would reopen.
        if (!next && breakdownOpen) ops.toggleBreakdown();
      }}
      sheet={sheet}
    >
      {breakdownOpen && <ScoreBreakdown record={record} ops={ops} title={prominence === "fit_only" ? (s.blocked ? "Would score 0 today, blocked" : `Would score ${s.score} today · ${s.band}`) : null} />}
      <div className="rec-kv rec-kv--96 rec-fit">
        {view.rows.map((r) => (
          <React.Fragment key={r.key}>
            <span>{r.label}</span>
            {r.breakdown ? (
              <span className="rec-stack">
                <span>
                  {r.value} ·{" "}
                  <button type="button" className="rec-link" aria-expanded={breakdownOpen} onClick={ops.toggleBreakdown}>Breakdown</button>
                </span>
                {r.sub && <span className="rec-sub">{r.sub}</span>}
              </span>
            ) : r.value ? (
              <span>{r.value}</span>
            ) : r.add ? (
              <AddFact add={r.add} hint={hintFor(r.key)} onSave={(v) => save({ [r.add.prop]: v })} />
            ) : (
              <span className="rec-empty">not recorded</span>
            )}
          </React.Fragment>
        ))}
        <span className="rec-kv__wide rec-fit__edit">
          <button type="button" className="rec-link" onClick={(e) => toProfile(e, ops)}>Edit</button>
        </span>
      </div>
    </RailGroup>
  );
}
